import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { fetchWordPressAssetStatus } from '../modules/wordpressPublishing/service';
import type { WordPressAssetStatus } from '../modules/wordpressPublishing/types';
import {
  getWordPressUiStatusColor,
  getWordPressUiStatusFromAssetStatus,
  getWordPressUiStatusLabel,
} from '../modules/wordpressPublishing/uiStatus';
import { formatWordPressSyncWarningCount } from '../modules/wordpressPublishing/syncWarningsUi';

const SELECTION_ASSET_KEY = 'wpPublishing:selectedAssetId';
const SELECTION_TYPE_KEY = 'wpPublishing:selectedType';
const ASSET_STATUS_CHIP_SX = {
  height: 24,
  borderRadius: '12px',
  '& .MuiChip-label': {
    px: 0.9,
    fontSize: '0.74rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
} as const;

function getPendingChangeMessage(status: WordPressAssetStatus | null): string {
  if (!status?.hasPendingChanges) return '';
  if (status.pendingImageChange && status.pendingContentChange && status.pendingQuoteBackgroundChange) {
    return 'Bilden, texten och quote bakgrundsbilden har ändrats sedan senaste WordPress-sync. Skapa ny WP-bild och publicera sedan om.';
  }
  if (status.pendingImageChange && status.pendingQuoteBackgroundChange) {
    return 'Bilden och quote bakgrundsbilden har ändrats sedan senaste WordPress-sync. Skapa ny WP-bild och publicera sedan om.';
  }
  if (status.pendingContentChange && status.pendingQuoteBackgroundChange) {
    return 'Text eller quote bakgrundsbild har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.';
  }
  if (status.pendingImageChange && status.pendingContentChange) {
    return 'Bilden och texten har ändrats sedan senaste WordPress-sync. Skapa ny WP-bild och publicera sedan om.';
  }
  if (status.pendingImageChange) {
    return 'En ny bildversion finns. Skapa en ny WP-bild innan du publicerar om till WordPress.';
  }
  if (status.pendingQuoteBackgroundChange) {
    return 'Quote bakgrundsbilden har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.';
  }
  if (status.pendingContentChange) {
    return 'Text eller templatefält har ändrats sedan senaste WordPress-sync. Publicera om ändringarna till WordPress.';
  }
  return '';
}

interface AssetWordPressPanelProps {
  assetId: string;
}

const AssetWordPressPanel: React.FC<AssetWordPressPanelProps> = ({ assetId }) => {
  const [status, setStatus] = useState<WordPressAssetStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uiStatus = getWordPressUiStatusFromAssetStatus(status);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const nextStatus = await fetchWordPressAssetStatus(assetId);
        if (active) {
          setStatus(nextStatus);
        }
      } catch (loadError: any) {
        if (active) {
          setError(loadError?.response?.data?.message || loadError?.message || 'Kunde inte läsa WordPress-status.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (assetId) {
      load();
    }

    return () => {
      active = false;
    };
  }, [assetId]);

  if (!assetId) return null;

  return (
    <Paper elevation={0} sx={{ p: 1.5, mb: 3, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ flexShrink: 0 }}>WordPress</Typography>
        {!loading && !error && status && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flex: 1,
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              flexWrap: 'nowrap',
              alignItems: 'center',
              pr: 0.5,
            }}
          >
            <Chip
              size="small"
              label={getWordPressUiStatusLabel(uiStatus)}
              color={getWordPressUiStatusColor(uiStatus)}
              sx={ASSET_STATUS_CHIP_SX}
            />
            {(status.activeSyncWarningCount || 0) > 0 && (
              <Tooltip title="WordPress rapporterade värden som saknas där. Öppna i modulen för detaljer.">
                <Chip
                  size="small"
                  label={formatWordPressSyncWarningCount(status.activeSyncWarningCount || 0)}
                  color="warning"
                  variant="outlined"
                  sx={ASSET_STATUS_CHIP_SX}
                />
              </Tooltip>
            )}
            {status.pendingQuoteBackgroundChange && (
              <Chip
                size="small"
                label="Quote bakgrundsbild ändrad"
                color="warning"
                variant="outlined"
                sx={ASSET_STATUS_CHIP_SX}
              />
            )}
            {status.quoteBackground?.usesFallback && (
              <Chip
                size="small"
                label="Fallback används"
                color="info"
                variant="outlined"
                sx={ASSET_STATUS_CHIP_SX}
              />
            )}
            {status.quoteBackground?.missing && (
              <Chip
                size="small"
                label="Quote bakgrundsbild saknas"
                color="default"
                variant="outlined"
                sx={ASSET_STATUS_CHIP_SX}
              />
            )}
          </Stack>
        )}
        <Tooltip title="Öppna i modulen">
          <span>
            <IconButton
              size="small"
              disabled={!status}
              onClick={() => {
                if (!status) return;
                window.sessionStorage.setItem(SELECTION_ASSET_KEY, assetId);
                window.sessionStorage.setItem(SELECTION_TYPE_KEY, status.role === 'legacy' ? 'matching' : status.type);
                window.location.hash = 'wordpress-publishing';
              }}
              sx={{ width: 34, height: 34, flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Laddar WordPress-status...</Typography>
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && status?.hasPendingChanges && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {getPendingChangeMessage(status)}
        </Alert>
      )}

      {!loading && !error && !status && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Den här asseten är inte kopplad till WordPress-flödet ännu.
        </Typography>
      )}
    </Paper>
  );
};

export default AssetWordPressPanel;
